// redux/slices/servicesSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl } from "../../Utils/serverSelector";
import { apiRequest } from "../../Utils/apiClient";
import { clearJwtToken } from "../../Utils/authStorage";

/* ===================== HELPERS ===================== */

const sortServices = (list = []) =>
  [...list]
    .filter((service) => service && service.id)
    .sort((a, b) => {
      const ao = a.sortOrder ?? a.id ?? 0;
      const bo = b.sortOrder ?? b.id ?? 0;
      return ao - bo;
    });

const normalizeError = (error, fallback) =>
  error?.response?.data ||
  error?.message ||
  fallback;

const handleAuthError = async (error) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    try {
      await clearJwtToken();
    } catch {}
  }
};

const updateServiceEverywhere = (state, updated) => {
  if (!updated?.id) return;

  const id = String(updated.id);

  const flatIndex = state.services.findIndex(
    (service) => String(service?.id) === id
  );

  if (flatIndex !== -1) {
    state.services[flatIndex] = {
      ...state.services[flatIndex],
      ...updated,
    };
  } else {
    state.services.push(updated);
  }

  Object.keys(state.byUid || {}).forEach((uid) => {
    const arr = state.byUid[uid] || [];
    const index = arr.findIndex((service) => String(service?.id) === id);

    if (index !== -1) {
      arr[index] = {
        ...arr[index],
        ...updated,
      };
      state.byUid[uid] = sortServices(arr);
    }
  });

  state.services = sortServices(state.services);
};

/* ===================== PRIVATE THUNKS ===================== */

export const fetchServices = createAsyncThunk(
  "services/fetchServices",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await apiRequest(
        "GET",
        `/services/${encodeURIComponent(uid)}`
      );

      const data = response?.data || response;

      return {
        uid: String(uid),
        services: Array.isArray(data) ? sortServices(data) : [],
      };
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(normalizeError(error, "Fetch services failed"));
    }
  }
);

export const fetchServicesByUid = createAsyncThunk(
  "services/fetchServicesByUid",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await apiRequest(
        "GET",
        `/services/${encodeURIComponent(uid)}`
      );

      const data = response?.data || response;

      return {
        uid: String(uid),
        services: Array.isArray(data) ? sortServices(data) : [],
      };
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(
        normalizeError(error, "Fetch services by uid failed")
      );
    }
  }
);

export const fetchServicesForUids = createAsyncThunk(
  "services/fetchServicesForUids",
  async (uids = [], { dispatch }) => {
    const uniqueUids = Array.from(new Set((uids || []).map(String))).filter(
      Boolean
    );

    const results = await Promise.allSettled(
      uniqueUids.map((uid) => dispatch(fetchServicesByUid(uid)).unwrap())
    );

    return results;
  }
);

export const addService = createAsyncThunk(
  "services/addService",
  async (service, { rejectWithValue }) => {
    try {
      const response = await apiRequest("POST", "/services", service);
      const data = response?.data || response;

      return data?.service || data;
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(normalizeError(error, "Add service failed"));
    }
  }
);

export const updateService = createAsyncThunk(
  "services/updateService",
  async ({ uid, serviceNameOld, newService }, { rejectWithValue }) => {
    try {
      if (!uid || !serviceNameOld) {
        throw new Error("UID и старое имя услуги обязательны");
      }

      const response = await apiRequest(
        "PUT",
        `/services/${encodeURIComponent(uid)}/${encodeURIComponent(
          serviceNameOld
        )}`,
        newService
      );

      const data = response?.data || response;

      return {
        uid: String(uid),
        updated: data?.service || data,
      };
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(normalizeError(error, "Update service failed"));
    }
  }
);

export const deleteService = createAsyncThunk(
  "services/deleteService",
  async ({ uid, serviceName }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      if (!serviceName) throw new Error("serviceName обязателен");

      await apiRequest(
        "DELETE",
        `/services/${encodeURIComponent(uid)}/${encodeURIComponent(
          serviceName
        )}`
      );

      return {
        uid: String(uid),
        serviceName,
      };
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(normalizeError(error, "Delete service failed"));
    }
  }
);

export const updateServiceDiscount = createAsyncThunk(
  "services/updateServiceDiscount",
  async (
    { id, discountType, discountValue, discountOnlyNewClients },
    { rejectWithValue }
  ) => {
    try {
      if (!id) throw new Error("ID услуги не передан");

      const response = await apiRequest(
        "PATCH",
        `/services/${encodeURIComponent(id)}/discount`,
        {
          discountType,
          discountValue,
          discountOnlyNewClients,
        }
      );

      const data = response?.data || response;

      return data?.service || data;
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(
        normalizeError(error, "Update service discount failed")
      );
    }
  }
);

export const reorderServices = createAsyncThunk(
  "services/reorderServices",
  async ({ uid, order }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await apiRequest(
        "POST",
        `/services/${encodeURIComponent(uid)}/reorder`,
        { order }
      );

      const data = response?.data || response;

      return {
        uid: String(uid),
        services: data?.services || data?.data || data,
      };
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(normalizeError(error, "Reorder services failed"));
    }
  }
);

/* ===================== PUBLIC THUNKS ===================== */

export const fetchPublicServices = createAsyncThunk(
  "services/fetchPublicServices",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const base = getServerUrl().replace(/\/$/, "");

      const { data } = await axios.get(
        `${base}/public/${encodeURIComponent(uid)}/services/`
      );

      return {
        uid: String(uid),
        services: Array.isArray(data) ? sortServices(data) : [],
      };
    } catch (error) {
      return rejectWithValue(
        normalizeError(error, "Fetch public services failed")
      );
    }
  }
);

export const fetchPublicServicesForUids = createAsyncThunk(
  "services/fetchPublicServicesForUids",
  async (uids = [], { dispatch }) => {
    const uniqueUids = Array.from(new Set((uids || []).map(String))).filter(
      Boolean
    );

    const results = await Promise.allSettled(
      uniqueUids.map((uid) => dispatch(fetchPublicServices(uid)).unwrap())
    );

    return results;
  }
);

/* ===================== STATE ===================== */

const initialState = {
  services: [],
  status: "idle",
  error: null,

  byUid: {},
  statusByUid: {},
  errorByUid: {},
};

/* ===================== SLICE ===================== */

const servicesSlice = createSlice({
  name: "services",
  initialState,
  reducers: {
    clearServicesError: (state) => {
      state.error = null;
    },

    resetServicesStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },

    clearServices: (state) => {
      state.services = [];
      state.byUid = {};
      state.statusByUid = {};
      state.errorByUid = {};
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServices.pending, (state, action) => {
        const uid = String(action.meta.arg);

        state.status = "loading";
        state.error = null;
        state.statusByUid[uid] = "loading";
        state.errorByUid[uid] = null;
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        const { uid, services } = action.payload || {};

        state.status = "succeeded";
        state.services = sortServices(services || []);

        if (uid) {
          state.byUid[uid] = sortServices(services || []);
          state.statusByUid[uid] = "succeeded";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchServices.rejected, (state, action) => {
        const uid = String(action.meta?.arg || "");
        const msg =
          action.payload || action.error?.message || "Fetch services failed";

        state.status = "failed";
        state.error = msg;

        if (uid) {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = msg;
          state.byUid[uid] = state.byUid[uid] || [];
        }
      })

      .addCase(fetchServicesByUid.pending, (state, action) => {
        const uid = String(action.meta.arg);

        state.statusByUid[uid] = "loading";
        state.errorByUid[uid] = null;
      })
      .addCase(fetchServicesByUid.fulfilled, (state, action) => {
        const { uid, services } = action.payload || {};

        if (!uid) return;

        state.byUid[uid] = sortServices(services || []);
        state.statusByUid[uid] = "succeeded";
        state.errorByUid[uid] = null;
      })
      .addCase(fetchServicesByUid.rejected, (state, action) => {
        const uid = String(action.meta?.arg || "");
        const msg =
          action.payload ||
          action.error?.message ||
          "Fetch services by uid failed";

        if (!uid) return;

        state.statusByUid[uid] = "failed";
        state.errorByUid[uid] = msg;
        state.byUid[uid] = state.byUid[uid] || [];
      })

      .addCase(fetchServicesForUids.fulfilled, () => {})

      .addCase(addService.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addService.fulfilled, (state, action) => {
        const created = action.payload;

        state.status = "succeeded";

        if (!created?.id) return;

        state.services.push(created);
        state.services = sortServices(state.services);

        const uid = String(created?.uid || "");
        if (uid) {
          if (!state.byUid[uid]) state.byUid[uid] = [];
          state.byUid[uid].push(created);
          state.byUid[uid] = sortServices(state.byUid[uid]);
        }
      })
      .addCase(addService.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "Add service failed";
      })

      .addCase(updateService.pending, (state, action) => {
        const uid = String(action.meta.arg?.uid || "");

        state.status = "loading";
        state.error = null;

        if (uid) state.statusByUid[uid] = "loading";
      })
      .addCase(updateService.fulfilled, (state, action) => {
        const { uid, updated } = action.payload || {};

        state.status = "succeeded";

        updateServiceEverywhere(state, updated);

        if (uid) {
          state.statusByUid[uid] = "succeeded";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(updateService.rejected, (state, action) => {
        const uid = String(action.meta.arg?.uid || "");
        const msg =
          action.payload || action.error?.message || "Update service failed";

        state.status = "failed";
        state.error = msg;

        if (uid) {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = msg;
        }
      })

      .addCase(updateServiceDiscount.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateServiceDiscount.fulfilled, (state, action) => {
        state.status = "succeeded";
        updateServiceEverywhere(state, action.payload);
      })
      .addCase(updateServiceDiscount.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Update service discount failed";
      })

      .addCase(deleteService.pending, (state, action) => {
        const uid = String(action.meta.arg?.uid || "");

        state.status = "loading";
        state.error = null;

        if (uid) state.statusByUid[uid] = "loading";
      })
      .addCase(deleteService.fulfilled, (state, action) => {
        const { uid, serviceName } = action.payload || {};

        state.status = "succeeded";

        state.services = state.services.filter(
          (service) => service?.serviceName !== serviceName
        );

        if (uid && serviceName) {
          state.byUid[uid] = (state.byUid[uid] || []).filter(
            (service) => service?.serviceName !== serviceName
          );
          state.statusByUid[uid] = "succeeded";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(deleteService.rejected, (state, action) => {
        const uid = String(action.meta.arg?.uid || "");
        const msg =
          action.payload || action.error?.message || "Delete service failed";

        state.status = "failed";
        state.error = msg;

        if (uid) {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = msg;
        }
      })

      .addCase(reorderServices.pending, (state, action) => {
        const uid = String(action.meta.arg?.uid || "");

        state.status = "loading";
        state.error = null;

        if (uid) state.statusByUid[uid] = "loading";
      })
      .addCase(reorderServices.fulfilled, (state, action) => {
        const { uid, services } = action.payload || {};
        const list = Array.isArray(services) ? sortServices(services) : null;

        state.status = "succeeded";

        if (list) {
          state.services = list;

          if (uid) {
            state.byUid[uid] = list;
            state.statusByUid[uid] = "succeeded";
            state.errorByUid[uid] = null;
          }
        } else {
          state.services = sortServices(state.services);

          if (uid && state.byUid[uid]) {
            state.byUid[uid] = sortServices(state.byUid[uid]);
          }
        }
      })
      .addCase(reorderServices.rejected, (state, action) => {
        const uid = String(action.meta.arg?.uid || "");
        const msg =
          action.payload || action.error?.message || "Reorder services failed";

        state.status = "failed";
        state.error = msg;

        if (uid) {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = msg;
        }
      })

      .addCase(fetchPublicServices.pending, (state, action) => {
        const uid = String(action.meta.arg);

        state.status = "loading";
        state.error = null;
        state.statusByUid[uid] = "loading";
        state.errorByUid[uid] = null;
      })
      .addCase(fetchPublicServices.fulfilled, (state, action) => {
        const { uid, services } = action.payload || {};

        state.status = "succeeded";
        state.services = sortServices(services || []);

        if (uid) {
          state.byUid[uid] = sortServices(services || []);
          state.statusByUid[uid] = "succeeded";
          state.errorByUid[uid] = null;
        }
      })
      .addCase(fetchPublicServices.rejected, (state, action) => {
        const uid = String(action.meta?.arg || "");
        const msg =
          action.payload ||
          action.error?.message ||
          "Fetch public services failed";

        state.status = "failed";
        state.error = msg;

        if (uid) {
          state.statusByUid[uid] = "failed";
          state.errorByUid[uid] = msg;
          state.byUid[uid] = state.byUid[uid] || [];
        }
      })

      .addCase(fetchPublicServicesForUids.fulfilled, () => {})

      .addCase(resetStore, () => initialState);
  },
});

/* ===================== ACTIONS ===================== */

export const {
  clearServicesError,
  resetServicesStatus,
  clearServices,
} = servicesSlice.actions;

export default servicesSlice.reducer;

/* ===================== SELECTORS ===================== */

export const selectServices = (state) => state?.services?.services || [];

export const selectServicesStatus = (state) =>
  state?.services?.status || "idle";

export const selectServicesError = (state) =>
  state?.services?.error || null;

export const selectServicesByUid = (state, uid) =>
  state?.services?.byUid?.[String(uid)] || [];

export const selectServicesStatusByUid = (state, uid) =>
  state?.services?.statusByUid?.[String(uid)] || "idle";

export const selectServicesErrorByUid = (state, uid) =>
  state?.services?.errorByUid?.[String(uid)] || null;

export const selectServicesForUids = createSelector(
  [(state) => state?.services?.byUid || {}, (state, uids) => uids || []],
  (byUid, uids) => {
    const result = [];

    for (const uid of uids || []) {
      const arr = byUid[String(uid)] || [];
      result.push(
        ...arr.map((service) => ({
          ...service,
          _uid: String(uid),
        }))
      );
    }

    return result;
  }
);

export const selectProfessionsFromServices = (
  servicesArray = [],
  lang = "ru"
) => {
  const currentLang = (lang || "ru").toLowerCase();
  const map = new Map();

  for (const service of servicesArray || []) {
    const key = String(service?.profession || "").trim();
    if (!key) continue;

    const ru = String(service?.profession || "").trim();
    const he = String(service?.professionHe || "").trim();
    const en = String(service?.professionEn || "").trim();

    let label = ru;

    if (currentLang === "he") {
      label = he || ru || en;
    } else if (currentLang === "en") {
      label = en || ru || he;
    }

    if (!map.has(key)) {
      map.set(key, label);
    }
  }

  return Array.from(map, ([key, label]) => ({
    key,
    label,
  }));
};